import type React from "react";
import type { TypographyProps } from "@mui/material/Typography";
import { formatPriceDisplay, formatPriceSimple } from "@/utils/price";

export interface PriceDisplayProps extends Omit<TypographyProps, "children"> {
  /**
   * Цена в копейках (как приходит с сервера)
   */
  value: number;
  /**
   * Формат отображения
   * - "currency" - полное форматирование с валютой (по умолчанию)
   * - "simple" - простая строка "100 ₽"
   */
  format?: "currency" | "simple";
  /**
   * Показывать ли валюту (только для format="currency")
   */
  showCurrency?: boolean;
  /**
   * Локаль для форматирования
   */
  locale?: string;
  /**
   * Валюта
   */
  currency?: string;
}

/**
 * Компонент для отображения цены
 * Автоматически конвертирует цену из копеек в рубли для отображения
 *
 * Символы валют (₽, €, $ и др.) отображаются через Noto Sans
 * с использованием CSS unicode-range для корректного рендеринга
 */
export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  value,
  format = "currency",
  showCurrency = true,
  locale = "ru-RU",
  currency = "RUB",
}) => {
  const formattedPrice =
    format === "simple"
      ? formatPriceSimple(value)
      : formatPriceDisplay(value, {
          locale,
          currency,
          showCurrency,
        });

  return formattedPrice;
};
